import { useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { Box } from '@mui/material';
import type { FetchBaseQueryError } from '@reduxjs/toolkit/query';
import type { SerializedError } from '@reduxjs/toolkit';

import { useChangePasswordMutation, useLogoutMutation } from '@/app/services/authApi';
import { useNotification } from '@/hooks/useNotification';
import { PasswordField } from '@/components/ui';
import { getErrorMessage } from '@/utils/errorUtils';
import {
  AuthHeader,
  AuthFieldLabel,
  AuthCodeInput,
  AuthPrimaryButton,
  AuthBottomNote,
} from './AuthPrimitives';
import { authInputSx } from './authStyles';

export interface ChangePasswordFormData {
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
}

export function ChangePasswordForm() {
  const [changePasswordMutation, { isLoading }] = useChangePasswordMutation();
  const [logoutMutation] = useLogoutMutation();
  const { showSuccessNotification, showErrorNotification } = useNotification();

  const [code, setCode] = useState<string[]>(['', '', '', '', '', '']);

  const {
    register,
    handleSubmit,
    formState: { errors },
    control,
    setError,
  } = useForm<ChangePasswordFormData>();

  // useWatch (subscription-based) instead of watch() — compatible with the React Compiler
  const newPassword = useWatch({ control, name: 'newPassword' });

  const onSubmit = async (data: ChangePasswordFormData) => {
    if (data.newPassword !== data.confirmPassword) {
      setError('confirmPassword', { type: 'manual', message: 'Passwords do not match' });
      return;
    }

    const codeString = code.join('');
    if (codeString.length !== 6) {
      showErrorNotification('Enter the 6-digit MFA code');
      return;
    }

    try {
      await changePasswordMutation({
        currentPassword: data.currentPassword,
        newPassword: data.newPassword,
        code: codeString,
      }).unwrap();
      showSuccessNotification('Password changed successfully');
    } catch (err) {
      const errorMessage =
        getErrorMessage(err as FetchBaseQueryError | SerializedError) ||
        'Failed to change password';
      showErrorNotification(errorMessage);
    }
  };

  return (
    <Box component="form" onSubmit={handleSubmit(onSubmit)} className="auth-change-password-form">
      <AuthHeader
        title="Set Your Password"
        subtitle="Replace the temporary password provided by your administrator."
      />

      <Box sx={{ mb: 4 }}>
        <AuthFieldLabel>Current (temporary) password</AuthFieldLabel>
        <PasswordField
          className="change-password-current"
          sx={authInputSx}
          id="currentPassword"
          placeholder="Enter password"
          autoComplete="current-password"
          autoFocus
          {...register('currentPassword', { required: 'Current password is required' })}
          error={!!errors.currentPassword}
          helperText={errors.currentPassword?.message}
        />
      </Box>

      <Box sx={{ mb: 4 }}>
        <AuthFieldLabel>New password*</AuthFieldLabel>
        <PasswordField
          className="change-password-new"
          sx={authInputSx}
          id="newPassword"
          placeholder="Enter password"
          autoComplete="new-password"
          {...register('newPassword', {
            required: 'New password is required',
            minLength: { value: 8, message: 'Password must be at least 8 characters' },
          })}
          error={!!errors.newPassword}
          helperText={
            errors.newPassword?.message || 'Min. 10 characters, upper & lower case, number, symbol'
          }
        />
      </Box>

      <Box sx={{ mb: 4 }}>
        <AuthFieldLabel>Confirm new password*</AuthFieldLabel>
        <PasswordField
          className="change-password-confirm"
          sx={authInputSx}
          id="confirmPassword"
          placeholder="Enter password"
          autoComplete="new-password"
          {...register('confirmPassword', {
            required: 'Please confirm your password',
            validate: (value) => value === newPassword || 'Passwords do not match',
          })}
          error={!!errors.confirmPassword}
          helperText={errors.confirmPassword?.message}
        />
      </Box>

      <Box sx={{ mb: 6 }}>
        <AuthFieldLabel>MFA code</AuthFieldLabel>
        <AuthCodeInput value={code} onChange={setCode} autoFocus={false} />
      </Box>

      <AuthPrimaryButton isLoading={isLoading} className="change-password-submit">
        Set Password & Continue
      </AuthPrimaryButton>

      <AuthBottomNote>
        Not you?{' '}
        <button type="button" onClick={() => logoutMutation()} className="change-password-signout">
          Sign in with a different account
        </button>
      </AuthBottomNote>
    </Box>
  );
}
